import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { Calendar } from "lucide-react";

interface ChangelogEntry {
  id: string;
  version: string;
  title: string;
  description: string;
  change_type: string;
  published_at: string;
  created_at: string;
}

export default function Changelog() {
  const [entries, setEntries] = useState<ChangelogEntry[]>([]);

  useEffect(() => {
    fetchChangelog();
  }, []);

  const fetchChangelog = async () => {
    const { data } = await supabase
      .from('changelog')
      .select('*')
      .eq('status', 'published')
      .order('published_at', { ascending: false });
    
    setEntries((data as any) || []);
  };

  const getChangeTypeBadge = (type: string) => {
    const variants: { [key: string]: "default" | "secondary" | "destructive" | "outline" } = {
      feature: "default",
      improvement: "secondary",
      bugfix: "outline",
      breaking: "destructive"
    };
    return <Badge variant={variants[type] || "default"}>{type}</Badge>;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container py-12 space-y-8">
        <div className="text-center space-y-4 max-w-3xl mx-auto">
          <h1 className="text-4xl font-bold text-foreground">What's New</h1>
          <p className="text-lg text-muted-foreground">
            Track the latest updates, features, and improvements we've made to enhance your PTA Hub experience
          </p>
        </div>

        <div className="max-w-4xl mx-auto space-y-4">
          {entries.map((entry) => (
            <Card key={entry.id} className="shadow-card border-l-4 border-l-primary hover:shadow-elevated transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-3 flex-wrap">
                      <CardTitle className="text-2xl">{entry.title}</CardTitle>
                      {getChangeTypeBadge(entry.change_type)}
                      <Badge variant="outline" className="font-mono text-xs">
                        v{entry.version}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      {new Date(entry.published_at).toLocaleDateString('en-GB', { 
                        day: 'numeric', 
                        month: 'long', 
                        year: 'numeric' 
                      })}
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-foreground leading-relaxed whitespace-pre-line">{entry.description}</p>
              </CardContent>
            </Card>
          ))}

          {entries.length === 0 && (
            <Card className="shadow-card">
              <CardContent className="py-16 text-center">
                <div className="max-w-sm mx-auto space-y-3">
                  <Calendar className="h-12 w-12 mx-auto text-muted-foreground" />
                  <h3 className="text-lg font-semibold text-foreground">No Updates Yet</h3>
                  <p className="text-muted-foreground">
                    We haven't published any changelog entries yet. Check back soon for updates!
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
